import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/lib/auth-context';
import { useSearchParams } from 'react-router-dom';
import { chatAPI, filesAPI, usersAPI, clientsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Send, Plus, Paperclip, MessageSquare, ClipboardList,
  Trash2, Pin, PinOff, Reply, Search, X, ThumbsUp, Check, Heart, Eye, Flame,
  Clock, FileCheck, ChevronDown, Users
} from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const REACTION_CONFIG = {
  thumbs_up: { icon: ThumbsUp, label: 'Like' },
  check: { icon: Check, label: 'Done' },
  heart: { icon: Heart, label: 'Love' },
  eyes: { icon: Eye, label: 'Seen' },
  fire: { icon: Flame, label: 'Fire' },
};

function dateSeparatorLabel(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function canDeleteMessage(msg, userId) {
  if (msg.deleted || msg.sender_id === 'system') return false;
  if (msg.sender_id !== userId) return false;
  const created = new Date(msg.created_at);
  return (Date.now() - created.getTime()) < 5 * 60 * 1000;
}

// ─── Chat Sidebar Group Item ────────────────────────
function GroupItem({ group, isSelected, onClick, isAdmin, onDelete }) {
  const lastMsg = group.last_message;
  return (
    <div
      className={`p-3 border-b cursor-pointer transition-colors duration-150 ${isSelected ? 'bg-accent' : 'hover:bg-muted/60'}`}
      onClick={onClick}
      data-testid="chat-group-item"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 min-w-0">
          {group.board_id && <ClipboardList className="h-3.5 w-3.5 text-teal-600 flex-shrink-0" />}
          <p className="font-medium text-sm truncate">{group.name}</p>
        </div>
        <div className="flex items-center gap-1">
          {group.unread_count > 0 && (
            <Badge className="h-5 min-w-[20px] px-1.5 text-[10px] bg-primary text-primary-foreground flex-shrink-0" data-testid="unread-badge">
              {group.unread_count > 99 ? '99+' : group.unread_count}
            </Badge>
          )}
          {isAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(group); }}
              className="p-0.5 rounded text-muted-foreground/40 hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
              style={{ opacity: isSelected ? 1 : undefined }}
              title="Delete chat"
              data-testid={`delete-chat-${group.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        {group.board_id && <Badge variant="outline" className="text-[9px] px-1 py-0 text-teal-700 border-teal-200">Task</Badge>}
        <p className="text-xs text-muted-foreground">{group.member_ids?.length || 0} members</p>
      </div>
      {lastMsg && (
        <p className="text-xs text-muted-foreground mt-1 truncate" data-testid="last-message-preview">
          <span className="font-medium">{lastMsg.sender_name}:</span> {lastMsg.content}
        </p>
      )}
    </div>
  );
}

// ─── Message Bubble ────────────────────────
function MessageBubble({ msg, isOwn, userId, canManage, onDelete, onReply, onReact, onPin }) {
  const isSystem = msg.sender_id === 'system';
  const deletable = canDeleteMessage(msg, userId);
  const [showActions, setShowActions] = useState(false);
  const reactions = msg.reactions || {};
  const hasReactions = Object.keys(reactions).length > 0;

  if (isSystem) {
    return (
      <div className="flex justify-center" data-testid="system-message">
        <div className="max-w-[80%] rounded-lg px-4 py-2 bg-teal-50 border border-teal-200 text-center">
          <p className="text-xs text-teal-700 font-medium">{msg.content}</p>
          <p className="text-[10px] text-teal-500 mt-0.5">
            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
    );
  }

  if (msg.deleted) {
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
        <div className="max-w-[70%] rounded-xl px-4 py-2.5 bg-muted/40 border border-dashed border-muted-foreground/20">
          <p className="text-sm italic text-muted-foreground">This message was deleted</p>
          <p className="text-[10px] mt-1 text-muted-foreground/50">
            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="relative max-w-[70%]">
        {/* Action bar */}
        {showActions && (
          <div className={`absolute -top-8 ${isOwn ? 'right-0' : 'left-0'} flex items-center gap-0.5 bg-card border rounded-lg shadow-sm px-1 py-0.5 z-10`} data-testid="message-actions">
            <TooltipProvider delayDuration={200}>
              {Object.entries(REACTION_CONFIG).map(([key, { icon: Icon, label }]) => (
                <Tooltip key={key}>
                  <TooltipTrigger asChild>
                    <button onClick={() => onReact(msg.id, key)} className="p-1 rounded hover:bg-muted transition-colors" data-testid={`react-${key}`}>
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">{label}</TooltipContent>
                </Tooltip>
              ))}
              <div className="w-px h-4 bg-border mx-0.5" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => onReply(msg)} className="p-1 rounded hover:bg-muted transition-colors" data-testid="reply-button">
                    <Reply className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">Reply</TooltipContent>
              </Tooltip>
              {canManage && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button onClick={() => onPin(msg.id)} className="p-1 rounded hover:bg-muted transition-colors" data-testid="pin-button">
                      {msg.pinned ? <PinOff className="h-3.5 w-3.5 text-amber-500" /> : <Pin className="h-3.5 w-3.5 text-muted-foreground" />}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">{msg.pinned ? 'Unpin' : 'Pin'}</TooltipContent>
                </Tooltip>
              )}
              {deletable && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button onClick={() => onDelete(msg.id)} className="p-1 rounded hover:bg-red-50 transition-colors" data-testid="delete-message-button">
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">Delete</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </div>
        )}

        {/* Bubble */}
        <div className={`rounded-xl px-4 py-2.5 ${isOwn ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'} ${msg.pinned ? 'ring-2 ring-amber-400/50' : ''}`}>
          {msg.pinned && <div className="flex items-center gap-1 mb-1"><Pin className="h-3 w-3 text-amber-500" /><span className="text-[10px] font-medium opacity-70">Pinned</span></div>}
          {!isOwn && <p className="text-xs font-medium mb-1 opacity-70">{msg.sender_name}</p>}

          {/* Reply preview */}
          {msg.reply_to && (
            <div className={`text-xs rounded-md px-2.5 py-1.5 mb-2 border-l-2 ${isOwn ? 'bg-primary-foreground/10 border-primary-foreground/40' : 'bg-muted/50 border-muted-foreground/40'}`}>
              <p className="font-medium opacity-70">{msg.reply_to.sender_name}</p>
              <p className="truncate opacity-60">{msg.reply_to.content}</p>
            </div>
          )}

          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
          <p className={`text-[10px] mt-1 ${isOwn ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>

        {/* Reactions */}
        {hasReactions && (
          <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
            {Object.entries(reactions).map(([emoji, userIds]) => {
              const Cfg = REACTION_CONFIG[emoji];
              if (!Cfg || userIds.length === 0) return null;
              const Icon = Cfg.icon;
              const isMine = userIds.includes(userId);
              return (
                <button
                  key={emoji}
                  onClick={() => onReact(msg.id, emoji)}
                  className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border transition-colors ${isMine ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-muted/60 border-transparent hover:bg-muted'}`}
                  data-testid={`reaction-badge-${emoji}`}
                >
                  <Icon className="h-3 w-3" />{userIds.length}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Status Tiles for Chat Header ────────────────────────
function StatusTiles({ clientStatus }) {
  if (!clientStatus) return null;

  const tiles = [
    { key: 'pending', label: 'Docs Pending', count: clientStatus.pending.length, items: clientStatus.pending, color: 'bg-amber-50 border-amber-200 text-amber-800', badgeColor: 'bg-amber-500', icon: Clock },
    { key: 'processing', label: 'Processing', count: clientStatus.processing.length, items: clientStatus.processing, color: 'bg-blue-50 border-blue-200 text-blue-800', badgeColor: 'bg-blue-500', icon: FileCheck },
    { key: 'filed', label: 'Filed', count: clientStatus.filed.length, items: clientStatus.filed, color: 'bg-emerald-50 border-emerald-200 text-emerald-800', badgeColor: 'bg-emerald-500', icon: Check },
  ];

  const STATUS_DISPLAY = {
    not_started: 'Not Started', documents_pending: 'Docs Pending', documents_received: 'Docs Received',
    ready_for_review: 'Ready for Review', ready_to_file: 'Ready to File', filed: 'Filed',
  };

  return (
    <div className="flex items-center gap-2 mt-2" data-testid="status-tiles">
      {tiles.map(tile => (
        <DropdownMenu key={tile.key}>
          <DropdownMenuTrigger asChild>
            <button className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors hover:opacity-80 ${tile.color}`} data-testid={`status-tile-${tile.key}`}>
              <tile.icon className="h-3.5 w-3.5" />
              <span>{tile.label}</span>
              <span className={`${tile.badgeColor} text-white rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center text-[10px] font-bold`}>{tile.count}</span>
              <ChevronDown className="h-3 w-3 opacity-60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72 max-h-64 overflow-y-auto p-0">
            <div className="p-2 border-b bg-muted/30">
              <p className="text-xs font-semibold text-muted-foreground">{tile.label} ({tile.count})</p>
            </div>
            {tile.items.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground text-center">No items</div>
            ) : (
              <div className="py-1">
                {tile.items.map((item) => (
                  <div key={`${tile.key}-${item.subtask}-${item.board}`} className="px-3 py-1.5 hover:bg-muted/50 transition-colors">
                    <p className="text-sm font-medium">{item.subtask}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-muted-foreground">{item.board}</span>
                      {tile.key !== 'pending' && (
                        <span className="text-[10px] px-1.5 py-0 rounded-full bg-muted font-medium">{STATUS_DISPLAY[item.status] || item.status}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ))}
    </div>
  );
}

// ─── Main Chat Page ────────────────────────
const ChatPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [clientStatus, setClientStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const autoSelectDone = useRef(false);
  const canCreate = ['admin', 'staff'].includes(user?.role);
  const canManage = ['admin', 'staff'].includes(user?.role);

  const loadGroups = useCallback(async () => {
    try {
      const res = await chatAPI.listGroups();
      setGroups(res.data);
    } catch (err) { logger.error('Failed to load groups', err); }
  }, []);

  const loadMessages = useCallback(async (groupId, search = '', pinnedOnly = false) => {
    try {
      const res = await chatAPI.getMessages(groupId, 200, search, pinnedOnly);
      setMessages(res.data);
    } catch (err) { logger.error('Failed to load messages', err); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadGroups();
      if (canCreate) {
        try {
          const [uRes, cRes] = await Promise.all([usersAPI.list(), clientsAPI.list()]);
          setUsers(uRes.data);
          setClients(cRes.data);
        } catch (err) { logger.error('Failed to load users/clients', err); }
      }
      setLoading(false);
    };
    init();
  }, [canCreate, loadGroups]);

  useEffect(() => {
    const groupId = searchParams.get('group');
    if (groupId && groups.length > 0 && !autoSelectDone.current) {
      const found = groups.find(g => g.id === groupId);
      if (found) {
        setSelectedGroup(found);
        autoSelectDone.current = true;
        setSearchParams({}, { replace: true });
      }
    }
  }, [groups, searchParams, setSearchParams]);

  useEffect(() => {
    if (selectedGroup) {
      loadMessages(selectedGroup.id, searchQuery, showPinnedOnly);
      const interval = setInterval(() => loadMessages(selectedGroup.id, searchQuery, showPinnedOnly), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedGroup, loadMessages, searchQuery, showPinnedOnly]);

  useEffect(() => {
    if (selectedGroup?.client_id) {
      chatAPI.getClientStatus(selectedGroup.id).then(res => setClientStatus(res.data)).catch(() => setClientStatus(null));
    } else {
      setClientStatus(null);
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (!showSearch && !showPinnedOnly) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, showSearch, showPinnedOnly]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedGroup) return;
    setSending(true);
    try {
      await chatAPI.sendMessage(selectedGroup.id, { content: newMessage, file_ids: [], reply_to_id: replyTo?.id || null });
      setNewMessage('');
      setReplyTo(null);
      await loadMessages(selectedGroup.id, searchQuery, showPinnedOnly);
      loadGroups();
    } catch (err) { toast.error('Failed to send message'); }
    setSending(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedGroup) return;
    try {
      const uploadRes = await filesAPI.upload(file);
      await chatAPI.sendMessage(selectedGroup.id, {
        content: `Shared file: ${file.name}`,
        file_ids: [uploadRes.data.id],
        reply_to_id: null
      });
      toast.success('File shared');
      await loadMessages(selectedGroup.id);
      loadGroups();
    } catch (err) { toast.error('Failed to upload file'); }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await chatAPI.createGroup({ name: newGroupName, client_id: selectedClient || null, member_ids: selectedMembers });
      toast.success('Chat group created');
      setCreateOpen(false);
      setNewGroupName('');
      setSelectedClient('');
      setSelectedMembers([]);
      loadGroups();
    } catch (err) { toast.error('Failed to create group'); }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await chatAPI.deleteMessage(selectedGroup.id, messageId);
      toast.success('Message deleted');
      await loadMessages(selectedGroup.id, searchQuery, showPinnedOnly);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete message'); }
  };

  const handleReact = async (messageId, emoji) => {
    try {
      await chatAPI.reactToMessage(selectedGroup.id, messageId, emoji);
      await loadMessages(selectedGroup.id, searchQuery, showPinnedOnly);
    } catch (err) { logger.error('Failed to react', err); }
  };

  const handlePin = async (messageId) => {
    try {
      await chatAPI.pinMessage(selectedGroup.id, messageId);
      await loadMessages(selectedGroup.id, searchQuery, showPinnedOnly);
    } catch (err) { toast.error('Failed to pin message'); }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete chat "${group.name}" and all its messages? This cannot be undone.`)) return;
    try {
      await chatAPI.deleteGroup(group.id);
      toast.success(`Chat "${group.name}" deleted`);
      if (selectedGroup?.id === group.id) setSelectedGroup(null);
      loadGroups();
    } catch (err) { toast.error('Failed to delete chat'); }
  };

  // Compute date separators
  const messagesWithSeparators = useMemo(() => {
    const result = [];
    let lastDate = '';
    for (const msg of messages) {
      const d = new Date(msg.created_at).toDateString();
      if (d !== lastDate) {
        result.push({ type: 'separator', date: msg.created_at, key: `sep-${d}` });
        lastDate = d;
      }
      result.push({ type: 'message', data: msg, key: msg.id });
    }
    return result;
  }, [messages]);

  if (loading) {
    return <div className="h-[70vh] bg-muted rounded-xl animate-pulse" />;
  }

  return (
    <div className="flex h-[calc(100vh-10rem)] rounded-xl border overflow-hidden bg-card">
      {/* Groups sidebar */}
      <div className="w-72 border-r flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-sm">Conversations</h3>
          {canCreate && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="create-chat-group">
                  <Plus className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New Chat Group</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateGroup} className="space-y-4">
                  <div>
                    <Label>Group Name *</Label>
                    <Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} required data-testid="chat-group-name" />
                  </div>
                  <div>
                    <Label>Client</Label>
                    <Select value={selectedClient} onValueChange={setSelectedClient}>
                      <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                      <SelectContent>
                        {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Add Members</Label>
                    <div className="space-y-1 max-h-40 overflow-y-auto border rounded-lg p-2">
                      {users.filter(u => u.id !== user?.id).map(u => (
                        <label key={u.id} className="flex items-center gap-2 text-sm p-1 hover:bg-muted rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedMembers.includes(u.id)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedMembers(prev => [...prev, u.id]);
                              else setSelectedMembers(prev => prev.filter(id => id !== u.id));
                            }}
                          />
                          {u.name} <Badge variant="secondary" className="text-xs">{u.role}</Badge>
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button type="submit" className="w-full">Create Group</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
        <ScrollArea className="flex-1">
          {groups.length === 0 ? (
            <div className="p-4 text-center">
              <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">No conversations yet</p>
            </div>
          ) : (
            groups.map(g => (
              <GroupItem
                key={g.id}
                group={g}
                isSelected={selectedGroup?.id === g.id}
                isAdmin={user?.role === 'admin'}
                onClick={() => { setSelectedGroup(g); setShowSearch(false); setSearchQuery(''); setShowPinnedOnly(false); setClientStatus(null); }}
                onDelete={handleDeleteGroup}
              />
            ))
          )}
        </ScrollArea>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col">
        {selectedGroup ? (
          <>
            {/* Chat header */}
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {selectedGroup.board_id && <ClipboardList className="h-4 w-4 text-teal-600 flex-shrink-0" />}
                  <h3 className="font-semibold text-sm">{selectedGroup.name}</h3>
                  {selectedGroup.board_id && <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-teal-700 border-teal-200">Task Thread</Badge>}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant={showPinnedOnly ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => { setShowPinnedOnly(!showPinnedOnly); setSearchQuery(''); setShowSearch(false); }}
                    title={showPinnedOnly ? 'Show all messages' : 'Show pinned only'}
                    data-testid="toggle-pinned"
                  >
                    <Pin className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={showSearch ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => { setShowSearch(!showSearch); if (showSearch) setSearchQuery(''); setShowPinnedOnly(false); }}
                    data-testid="toggle-search"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-xs text-muted-foreground hover:text-foreground hover:underline transition-colors flex items-center gap-1" data-testid="members-trigger">
                    <Users className="h-3 w-3" />
                    {selectedGroup.member_ids?.length || 0} members
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-56 p-0">
                  <div className="px-3 py-2 border-b bg-muted/30">
                    <p className="text-xs font-semibold text-muted-foreground">Participants ({selectedGroup.member_ids?.length || 0})</p>
                  </div>
                  <div className="py-1 max-h-52 overflow-y-auto">
                    {(selectedGroup.member_ids || []).map(mid => {
                      const member = users.find(u => u.id === mid);
                      return (
                        <div key={mid} className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50" data-testid="member-item">
                          <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary flex-shrink-0">
                            {member?.name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{member?.name || 'Unknown'}</p>
                          </div>
                          <Badge variant="secondary" className="text-[9px] px-1.5 py-0 flex-shrink-0">{member?.role || '-'}</Badge>
                        </div>
                      );
                    })}
                    {(!selectedGroup.member_ids || selectedGroup.member_ids.length === 0) && (
                      <p className="text-xs text-muted-foreground text-center py-3">No participants</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              {/* Search bar */}
              {showSearch && (
                <div className="flex items-center gap-2 mt-2" data-testid="search-bar">
                  <Input
                    placeholder="Search messages..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="h-8 text-sm"
                    autoFocus
                    data-testid="search-messages-input"
                  />
                  {searchQuery && (
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSearchQuery('')}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
              {showPinnedOnly && (
                <div className="flex items-center gap-2 mt-2 text-xs text-amber-600 font-medium">
                  <Pin className="h-3.5 w-3.5" /> Showing pinned messages only
                </div>
              )}
              {/* Client compliance status tiles */}
              {selectedGroup.client_id && clientStatus && (
                <StatusTiles clientStatus={clientStatus} />
              )}
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="chat-messages space-y-3">
                {messagesWithSeparators.map(item => {
                  if (item.type === 'separator') {
                    return (
                      <div key={item.key} className="flex items-center gap-3 py-2" data-testid="date-separator">
                        <div className="flex-1 border-t border-muted-foreground/15" />
                        <span className="text-[11px] font-medium text-muted-foreground px-2 bg-card">{dateSeparatorLabel(item.date)}</span>
                        <div className="flex-1 border-t border-muted-foreground/15" />
                      </div>
                    );
                  }
                  const msg = item.data;
                  const isOwn = msg.sender_id === user?.id;
                  return (
                    <MessageBubble
                      key={msg.id}
                      msg={msg}
                      isOwn={isOwn}
                      userId={user?.id}
                      canManage={canManage}
                      onDelete={handleDeleteMessage}
                      onReply={setReplyTo}
                      onReact={handleReact}
                      onPin={handlePin}
                    />
                  );
                })}
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm text-muted-foreground">
                      {searchQuery ? 'No messages match your search'
                        : showPinnedOnly ? 'No pinned messages'
                          : 'No messages yet. Start the conversation!'}
                    </p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Composer */}
            <div className="p-4 border-t">
              {/* Reply preview */}
              {replyTo && (
                <div className="flex items-center justify-between bg-muted/50 border-l-2 border-primary rounded-md px-3 py-2 mb-2" data-testid="reply-preview">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-primary">{replyTo.sender_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{replyTo.content}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0" onClick={() => setReplyTo(null)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
              <div className="flex gap-2">
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
                <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="chat-file-upload-button">
                  <Paperclip className="h-4 w-4" />
                </Button>
                <Input
                  placeholder={replyTo ? `Reply to ${replyTo.sender_name}...` : 'Type a message...'}
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  disabled={sending}
                  data-testid="chat-message-composer"
                />
                <Button onClick={handleSend} disabled={sending || !newMessage.trim()} data-testid="chat-send-button">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-1">Select a conversation</h3>
              <p className="text-sm text-muted-foreground">Choose a chat group to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
